import * as React from 'react';
import { assertNever } from 'owa-assert';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { useInnerFocusTracker } from 'owa-react-hooks/lib/useInnerFocusTracker';
import type { AssetId } from 'owa-timestream-types';
import { getAsset, resetIsNewlyCreated } from 'owa-timestream-store';
import { useSetDueDate, useSetIsComplete } from 'owa-timestream-asset-sdk-duedates';
import { isResizing, onAssetResizeEnd } from 'owa-timestream-asset-sdk-resize';
import { useTaskDueDateSync } from 'owa-timestream-asset-sdk-tasks';
import isTextInputElement from '../util/isTextInputElement';
import isCtrlOrCmdKeyDown from '../util/isCtrlOrCmdKeyDown';
import { useViewStateSelectors } from '../hooks/useViewStateSelectors';
import classNames from 'classnames';
import loc from 'owa-localize';
import { getAssetViewStyle, getOptionsPosition, getAssetDescriptor } from '../assetsCache';
import * as styles from '../components/styles.scss';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import { focusAsset } from 'owa-timestream-asset-sdk-actions';
import { useProjectId } from '../hooks/useProjectId';
import getCardThemeClassNames from 'owa-timestream-asset-sdk-color/lib/getCardThemeClassNames';
import { DefaultCommand } from 'owa-timestream-asset-sdk-types';
import { useMouseOrPrimaryPointerEvent } from 'owa-react-hooks/lib/useMouseOrPrimaryPointerEvent';
import { usePointerEvents } from 'owa-react-hooks/lib/usePointerEvents';
import { RESIZE_HANDLE_SIZE } from '../constants';
import useTrackAssetSize from './useTrackAssetSize';

export default function useAssetViewBaseViewState(
    assetId: AssetId,
    assetRef: React.RefObject<HTMLDivElement>,
    assetAttributes?: React.HTMLAttributes<HTMLDivElement> | null
) {
    const {
        clearSelectedAssets,
        isCanvasLocked,
        isMovingAssets,
        isSelectedAsset,
        selectAsset,
        startDraggingAssets,
        toggleAssetSelection,
    } = useViewStateSelectors();
    const projectId = useProjectId();
    const asset = getAsset(projectId, assetId);
    const { payload, type, isNewlyCreated, children, color } = asset;
    const isSelected = isSelectedAsset(assetId);
    const viewStyle = getAssetViewStyle(type);
    const descriptor = getAssetDescriptor(type);
    const colorName = descriptor?.getBackgroundColor?.(payload) || color || '';

    const cardThemeClassNames = getCardThemeClassNames(colorName);

    const className = classNames(
        {
            [styles.assetSelected]: isSelectedAsset(assetId),
            [styles.assetViewLocked]: isCanvasLocked(),
            [viewStyle || '']: !!viewStyle,
        },
        styles.assetHighContrast,
        assetAttributes?.className || '',
        ...cardThemeClassNames
    );

    const [shouldShowOptions, setShowOptions, setHideOptions] = useBooleanState(false);
    const [wasClicked, setWasClicked, resetWasClicked] = useBooleanState(false);
    const {
        hasFocusRef,
        hasFocusState,
        setHasFocus,
        onFocusCapture,
        onBlurCapture,
    } = useInnerFocusTracker(assetRef);

    const assetTitle = descriptor?.getTitleString(payload);
    const { dueDate, isComplete } = useTaskDueDateSync(
        projectId,
        assetId,
        assetTitle,
        descriptor?.preventFullControlOverTask
    );

    React.useEffect(() => {
        if (isNewlyCreated) {
            resetIsNewlyCreated(projectId, assetId);
            setHasFocus();

            const e = assetRef.current;
            if (e) {
                const focusOn = e.querySelector('input') || e.querySelector('textarea') || e;
                focusOn.focus();
            }
        }
    }, [projectId, assetId, isNewlyCreated, assetRef.current]);

    const [showCallOut] = React.useState<boolean | undefined>(isNewlyCreated);

    // use onFocus instead of hasFocus effect because hasFocus runs during capture phase, and we need to delay until onPointerDown has run
    const onFocus = React.useCallback(
        (e: React.FocusEvent<HTMLDivElement>) => {
            // focus is called as part of click, but we don't want this selection logic to run during clicks (handled by pointer down and up)
            if (wasClicked) {
                resetWasClicked();
                return;
            }

            // make sure target is a descendant of the current asset & not a child asset, otherwise unexpected selection parent/child assets will occur
            const closestParentAssetId = e.target
                ?.closest('[data-assetid]')
                ?.getAttribute('data-assetid');
            if (
                e.currentTarget.contains(e.target) &&
                closestParentAssetId === assetId &&
                hasFocusRef.current &&
                !isSelected
            ) {
                selectAsset(assetId);
            }
        },
        [assetId, hasFocusRef, isSelected, selectAsset, wasClicked, resetWasClicked]
    );

    useOrchestrator(
        focusAsset,
        ({ id }) => {
            if (assetId === id) {
                assetRef.current?.focus({ preventScroll: true });

                // when focus is called from here, onFocus callback doesn't fire, so we'll call select directly
                selectAsset(assetId);
                setHasFocus();
            }
        },
        [assetRef, assetId]
    );

    const onEffectiveBlurCapture = React.useCallback(
        (e: React.FocusEvent) => {
            onAssetResizeEnd();
            onBlurCapture(e);
        },
        [onBlurCapture]
    );

    const usingPointerEvents = usePointerEvents();
    const onMouseDown = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (e.button === 0) {
                setWasClicked();

                // Stop the Layer from panning
                e.stopPropagation();

                // Prevent FireFox from dragging images
                const targetElement = e.target as HTMLElement;
                if (targetElement.tagName == 'IMG') {
                    e.preventDefault();
                }

                const assetId = getAssetId(e);
                const isSelected = isSelectedAsset(assetId);
                const targetElt = e.target as HTMLInputElement;

                // If clicking on a link, do not drag the asset so link fires.
                // For now this will break moving asset by dragging from the link,
                // but it is better than not firing the link at all.
                if (targetElt.tagName.toLowerCase() === 'a') {
                    return;
                }

                const isTargetAnInputElement =
                    targetElt.tagName.toLowerCase() === 'input' || isTextInputElement(targetElt);

                if (isSelected && !isTargetAnInputElement) {
                    // avoid dragging if the asset is selected and is an input of sorts to allow for normal textbox behavior
                    // We might need to re-work when we start dragging assets to have a drag
                    // threshold, thus requiring the user to move a little more, like in drag-and-drop.
                    startDraggingAssets();
                    e.preventDefault();
                } else if (!isSelected && !isCtrlOrCmdKeyDown(e)) {
                    // select the asset & deselect all others
                    selectAsset(assetId);

                    // VSO 99052: If the target element is a text input, bring focus to it.
                    // We don't drag the asset to allow the user to select text.
                    // Otherwise, bring focus to the asset to ensure that if focus was previously on
                    // a text input element in another asset, that focus is shifted away.
                    //
                    // TODO VSO 100339: Write tests for the event handlers in this file.
                    if (isTargetAnInputElement) {
                        targetElt.focus();
                    } else {
                        focusAsset(projectId, assetId);
                        startDraggingAssets();
                        e.preventDefault();
                    }
                }
            }
        },
        [
            projectId,
            isSelectedAsset,
            startDraggingAssets,
            selectAsset,
            setWasClicked,
            assetRef.current,
        ]
    );

    const onPointerDown = React.useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (e.isPrimary && e.button == 0) {
                // Release pointer capture, so bucket hover targets get the pointerOver event.
                // This makes touch/pen work more like mouse events than when implicit capture is in effect.
                // See https://w3c.github.io/pointerevents/#implicit-pointer-capture
                const targetElement = e.target as HTMLElement;
                if (targetElement.hasPointerCapture(e.pointerId)) {
                    targetElement.releasePointerCapture(e.pointerId);
                }

                onMouseDown(e);
            }
        },
        [onMouseDown]
    );

    const [onMouseUp, onPointerUp] = useMouseOrPrimaryPointerEvent(
        React.useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                if (e.button === 0 && !isMovingAssets()) {
                    e.stopPropagation();

                    const assetId = getAssetId(e);
                    if (isCtrlOrCmdKeyDown(e)) {
                        // select additional assets
                        toggleAssetSelection(assetId);
                    } else {
                        // select the asset & deselect all others
                        selectAsset(assetId);
                    }
                }
            },
            [isMovingAssets, toggleAssetSelection, selectAsset]
        )
    );

    const onKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.currentTarget === e.target) {
                let handled = false;
                switch (e.which) {
                    case KeyCodes.enter:
                    case KeyCodes.space:
                        descriptor?.onAssetInvoked?.(payload);
                        handled = true;
                        break;
                    case KeyCodes.escape:
                        e.currentTarget.blur();
                        clearSelectedAssets();
                        handled = true;
                        break;
                }

                if (handled) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            } else {
                // if esc while focused inside asset, focus the asset itself
                if (e.which === KeyCodes.escape) {
                    assetRef.current?.focus();
                }
            }

            if (assetAttributes?.onKeyDown) {
                assetAttributes?.onKeyDown(e);
            }
        },
        [descriptor, payload, clearSelectedAssets, assetRef]
    );

    const onWheel = React.useCallback(
        (e: React.WheelEvent<HTMLDivElement>) => {
            const { currentTarget, target } = e;
            const isResizeable =
                !!descriptor?.assetResizeConfiguration &&
                !descriptor.assetResizeConfiguration(payload).resizeDisabled;
            const resizeHandleSpacing = isResizeable ? RESIZE_HANDLE_SIZE / 2 : 0;
            const isShowingVerticalScrollBar =
                currentTarget.scrollHeight - resizeHandleSpacing > currentTarget.clientHeight;
            const isShowingHorizontalScrollBar =
                currentTarget.scrollWidth - resizeHandleSpacing > currentTarget.clientWidth;
            if (
                !isResizing(assetId) &&
                (isShowingVerticalScrollBar ||
                    isShowingHorizontalScrollBar ||
                    isTextInputElement(e.target as HTMLInputElement)) &&
                currentTarget !== target
            ) {
                e.stopPropagation();
            }
        },
        [assetId, payload, descriptor?.assetResizeConfiguration]
    );

    const onDateSelected = useSetDueDate(
        projectId,
        assetId,
        !descriptor?.preventFullControlOverTask
    );
    const onIsCompletedChanged = useSetIsComplete(projectId, assetId);

    useTrackAssetSize(projectId, assetId, assetRef);

    return {
        baseAttributes: {
            // Apply attributes given by parent element (ex, TimeStreamAssetView)
            ...assetAttributes,
            // Then, override with the ones calculated for AssetViewBase
            className: styles.assetViewBase,
            style: {}, // override style from assetAttributes since it's going on the wrapper
        },
        wrapperAttributes: {
            id: assetId,
            className,
            style: {
                ...assetAttributes?.style,
            },
            'aria-label': descriptor?.getAriaLabel(asset.payload) ?? loc(asset.type),
            tabIndex: 0,
            'data-is-focusable': true,
            'data-assetid': assetId,
            onWheel: onWheel,

            onMouseDown: usingPointerEvents ? undefined : onMouseDown,
            onMouseUp: usingPointerEvents ? undefined : onMouseUp,
            onMouseEnter: usingPointerEvents ? undefined : setShowOptions,
            onMouseLeave: usingPointerEvents ? undefined : setHideOptions,

            onPointerDown: usingPointerEvents ? onPointerDown : undefined,
            onPointerUp: usingPointerEvents ? onPointerUp : undefined,
            onPointerEnter: usingPointerEvents ? setShowOptions : undefined,
            onPointerLeave: usingPointerEvents ? setHideOptions : undefined,

            onFocus: onFocus,
            onFocusCapture: onFocusCapture,
            onBlurCapture: onEffectiveBlurCapture,
            onKeyDown: onKeyDown,
        },
        viewProps: {
            payload,
            isSelected,
            isNewlyCreated,
            childrenAssets: children,
            color,
            lightningId: descriptor?.lightningId,
            inAppMessage: descriptor?.inAppMessage,
            showCallOut,
        },
        assetErrorProps: {
            assetType: type,
        },
        assetOptionsProps: {
            ...getOptionsPosition(type),
            shouldHideOptions: !(
                shouldShowOptions ||
                hasFocusState ||
                isResizing(assetId) ||
                isSelected
            ),
            onOptionsClosed: setHideOptions,
        },
        shouldShowDueDatePicker:
            !!asset.dueDate &&
            !descriptor?.unsupportedDefaultCommands.includes(DefaultCommand.DueDate),
        shouldAssetRenderDueDate: descriptor?.customRenderDueDate,
        shouldAssetRenderOptions: descriptor?.customRenderOptions,
        dueDatePickerProps: {
            date: dueDate,
            onDateSelected: onDateSelected,
            onDateRemoved: () => onDateSelected(null),
            isCompleted: isComplete,
            onIsCompletedChanged: onIsCompletedChanged,
        },
    };
}

function getAssetId(e: React.SyntheticEvent<HTMLDivElement>): AssetId {
    const assetId = e.currentTarget.dataset.assetid as AssetId | undefined;
    return assetId != undefined ? assetId : assertNever(assetId as never);
}
